import CreateEmailTemplatePage from '@/components/Drawer/CreateEmailTemplatePage'
import CreateWhatsAppTemplatePage from '@/components/Drawer/CreateWhatsAppTemplatePage'
import {Drawer} from '@/components/Drawer/Drawer'
import EmailTemplateDetails from '@/components/Drawer/EmailTemplateDetails'
import EmailTemplatePreview from '@/components/Drawer/EmailTemplatePreview'
import {useGetWhatsAppTemplateDetails} from '@/components/Drawer/hooks/useWhatsAppTemplateDetails'
import WhatsAppTemplateDetails from '@/components/Drawer/WhatsAppTemplateDetails'
import WhatsAppTemplatePreview from '@/components/Drawer/WhatsAppTemplatePreview'
import {useCountries} from '@/hooks/useCountries'
import {TemplateTabKey} from '@/pages/MessageTemplatesList'
import {store} from '@/store'
import {showNotification} from '@/store/slices'
import {MessageTemplateDrawerStatusEnum} from '@/types/MessageTemplateDrawerStatusEnum'
import {EmailTemplateStatus} from '@/types/EmailTemplate'
import {WhatsappTemplateStatus} from '@/types/WhatsappTemplate'
import {createMessageTemplateUseCase} from '@/useCases/createMessageTemplateUseCase'
import type {CreateMessageTemplateUseCaseParams} from '@/useCases/createMessageTemplateUseCase'
import type {MessageTemplateChannel} from '@/types/MessageTemplate'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import _ from 'lodash'
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {ROUTES} from '../../config/constants'

export type MessageTemplateDrawerMode = 'create' | 'details'
interface Props {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  mode: MessageTemplateDrawerMode
  templateType: TemplateTabKey
}

const getInitialDrawerStatus = (
  mode: MessageTemplateDrawerMode,
  templateType: TemplateTabKey
): MessageTemplateDrawerStatusEnum => {
  if (mode === 'details') {
    return templateType === 'email'
      ? MessageTemplateDrawerStatusEnum.emailTemplateDetails
      : MessageTemplateDrawerStatusEnum.whatsappTemplateDetails
  }

  return templateType === 'email'
    ? MessageTemplateDrawerStatusEnum.createEmailTemplate
    : MessageTemplateDrawerStatusEnum.createWhatsAppTemplate
}

const getInitialCategory = (templateType: TemplateTabKey): string => (templateType === 'email' ? 'RTN' : '')

export default function MessageTemplateCreationDrawer({
  isOpen,
  setIsOpen,
  mode,
  templateType,
}: Props): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {country} = useCountries()
  const navigate = useNavigate()
  const {id: whatsAppTemplateId} = useParams()

  const [templateName, setTemplateName] = useState<string>('')
  const [category, setCategory] = useState<string>(() => getInitialCategory(templateType))
  const [templateMessage, setTemplateMessage] = useState<string>('')
  const [drawerStatus, setDrawerStatus] = useState<MessageTemplateDrawerStatusEnum>(() =>
    getInitialDrawerStatus(mode, templateType)
  )

  const queryClient = useQueryClient()

  const {mutate} = useMutation({
    mutationKey: ['createMessageTemplate'],
    mutationFn: createMessageTemplateUseCase,
    onSuccess: (data, variables) => {
      const templateID = data?.id
      if (templateID) {
        navigate(ROUTES.MESSAGE_TEMPLATE.replace(':id', templateID.toString()))
      }
      store.dispatch(
        showNotification({
          show: true,
          type: 'success',
          title: 'Success',
          message: `${variables.channel} Template has been saved successfully`,
        })
      )
      queryClient.invalidateQueries({
        queryKey: [variables.channel === 'EMAIL' ? 'emailTemplates' : 'whatsappTemplates'],
      })
    },
    onError: (error, variables) => {
      store.dispatch(
        showNotification({
          show: true,
          type: 'error',
          title: 'Error',
          message: `Failed to save ${variables.channel} Template: ${error.message}`,
        })
      )
    },
  })

  const cleanUpAllFields = function (): void {
    setTemplateName('')
    setCategory(getInitialCategory(templateType))
    setTemplateMessage('')
    setDrawerStatus(getInitialDrawerStatus(mode, templateType))
  }

  const closeDrawer = function (): void {
    cleanUpAllFields()
    navigate(`${ROUTES.MESSAGE_TEMPLATES}`)
    setIsOpen(false)
  }

  const persistData = function (channel: MessageTemplateChannel, status: string): void {
    const request: CreateMessageTemplateUseCaseParams = {
      templateID: whatsAppTemplateId,
      name: templateName,
      scope: category,
      message: templateMessage,
      status,
      active: false,
      channel,
    }
    mutate(request)
  }

  const sendWhatsAppTemplate = function (): void {
    persistData('WHATSAPP', WhatsappTemplateStatus.PENDING_APPROVAL)
    closeDrawer()
  }

  const sendWhatsAppTemplateDraft = function (): void {
    persistData('WHATSAPP', WhatsappTemplateStatus.DRAFT)
    closeDrawer()
  }

  const sendEmailTemplate = function (): void {
    persistData('EMAIL', EmailTemplateStatus.APPROVED)
    closeDrawer()
  }

  const sendEmailTemplateDraft = function (): void {
    persistData('EMAIL', EmailTemplateStatus.DRAFT)
    closeDrawer()
  }

  const {data} = useGetWhatsAppTemplateDetails(whatsAppTemplateId)
  const templateDetailsData = {
    templateId: data?.id.toString() ?? '',
    templateName: data?.name ?? '',
    category: data?.scope ?? '',
    templateMessage: data?.message ?? '',
    templateStatus: data?.status ?? '',
  }

  const title = formatMessage({
    id:
      templateType === 'email'
        ? 'EMAIL_TEMPLATE_BUILDER.CREATE_EMAIL_TEMPLATE_HEADER'
        : 'WHATSAPP_TEMPLATE_BUILDER.CREATE_WHATSAPP_TEMPLATE_HEADER',
  })

  const isEmailTemplateDetails =
    drawerStatus === MessageTemplateDrawerStatusEnum.emailTemplateDetails && templateType === 'email'
  const isEmailTemplatePreview =
    drawerStatus === MessageTemplateDrawerStatusEnum.emailTemplatePreview && templateType === 'email'
  const drawerSize = isEmailTemplateDetails || isEmailTemplatePreview ? '800px' : '410px'

  useEffect(() => {
    if (isOpen) {
      setDrawerStatus(getInitialDrawerStatus(mode, templateType))
      setCategory(getInitialCategory(templateType))
    }
  }, [isOpen, mode, templateType])

  const drawStatusProvider = function (drawerStatus: MessageTemplateDrawerStatusEnum): React.JSX.Element {
    const commonProps = {
      ...templateDetailsData,
      ..._.omitBy(
        {
          templateName,
          category,
          templateMessage,
        },
        value => _.isNil(value) || value === ''
      ),
    }

    const updateFieldsProps = {
      setTemplateName,
      setCategory,
      setTemplateMessage,
    }
    switch (drawerStatus) {
      case MessageTemplateDrawerStatusEnum.createWhatsAppTemplate:
        return (
          <CreateWhatsAppTemplatePage
            {...commonProps}
            {...updateFieldsProps}
            country={country}
            updateDrawerStatus={(status: MessageTemplateDrawerStatusEnum) => setDrawerStatus(status)}
            closeDrawer={closeDrawer}
          />
        )
      case MessageTemplateDrawerStatusEnum.createEmailTemplate:
        return (
          <CreateEmailTemplatePage
            {...commonProps}
            {...updateFieldsProps}
            country={country}
            updateDrawerStatus={(status: MessageTemplateDrawerStatusEnum) => setDrawerStatus(status)}
            closeDrawer={closeDrawer}
          />
        )
      case MessageTemplateDrawerStatusEnum.whatsappTemplatePreview:
        return (
          <WhatsAppTemplatePreview
            {...commonProps}
            sendWhatsAppTemplateDraft={sendWhatsAppTemplateDraft}
            sendWhatsAppTemplate={sendWhatsAppTemplate}
          />
        )
      case MessageTemplateDrawerStatusEnum.emailTemplatePreview:
        return (
          <EmailTemplatePreview
            {...commonProps}
            country={country}
            updateDrawerStatus={(status: MessageTemplateDrawerStatusEnum) => setDrawerStatus(status)}
            sendEmailTemplateDraft={sendEmailTemplateDraft}
            sendEmailTemplate={sendEmailTemplate}
          />
        )
      case MessageTemplateDrawerStatusEnum.whatsappTemplateDetails:
        return (
          <WhatsAppTemplateDetails
            updateDrawerStatus={(status: MessageTemplateDrawerStatusEnum) => setDrawerStatus(status)}
          />
        )
      case MessageTemplateDrawerStatusEnum.emailTemplateDetails:
        return (
          <EmailTemplateDetails
            updateDrawerStatus={(status: MessageTemplateDrawerStatusEnum) => setDrawerStatus(status)}
          />
        )
    }
  }

  return (
    <Drawer isOpen={isOpen} headerTitle={title} onClose={closeDrawer} direction="right" size={drawerSize}>
      <div>{drawStatusProvider(drawerStatus)}</div>
    </Drawer>
  )
}
